/*
 * select-clinics-dialog-datasource.ts
 * utms-admin-dashboard
 * 
 * Last Modified: 26.07.2024
 * Modified By: Tetiana Makovii
 * 
 * Created by Tetiana Makovii on 23.04.2023
 * Copyright © 2026 Sonova AG. All rights reserved.
 */

import { DataSource } from "@angular/cdk/collections"
import { BehaviorSubject, first, Observable, skip, Subscription } from "rxjs"
import { ClinicListItem } from "src/app/common/models/users-data/clinic-list-item.model"
import { ClinicService } from "src/app/services/clinic.service"
import { SelectClinicsSortingHeadersEnum } from "./enums/select-clinics-sorting-headers-enum"
import { SelectClinicsSortingKeysEnum } from "./enums/select-clinics-sorting-keys-enum"
import { SortingDirectionsEnum } from "src/common-panel/enums/sorting-directions-enum"


export class SelectClinicsDialogDataSource extends DataSource<ClinicListItem>
{
    subscription: Subscription = new Subscription()

    private _data: ClinicListItem[] = []
    private _sortDirection = this.clinicService.tableSettings.sortDirection
    private _sortingHeader = this.clinicService.tableSettings.sortingHeader

    private _pageSize: number = this.clinicService.tableSettings.pageSize;
    private _pageIndex: number = this.clinicService.tableSettings.pageIndex;

    private clinicsSubject = new BehaviorSubject<ClinicListItem[]>([]);
    private loadingSubject = new BehaviorSubject<boolean>(false)

    public loading$ = this.loadingSubject.asObservable()

    get count()
    {
        return this._data.length
    }

    get data()
    {
        return this._data
    }

    set sortDirection(value: SortingDirectionsEnum)
    {
        this._sortDirection = value
        this.clinicService.tableSettings.sortDirection = value
    }

    get sortDirection(): SortingDirectionsEnum
    {
        return this._sortDirection
    }

    set sortingHeader(value: SelectClinicsSortingHeadersEnum)
    {
        this._sortingHeader = value
        this.clinicService.tableSettings.sortingHeader = value
    }

    get sortingHeader(): SelectClinicsSortingHeadersEnum
    {
        return this._sortingHeader
    }

    set pageSize(value: number)
    {
        this._pageSize = value
        this.clinicService.tableSettings.pageSize = value
    }

    get pageSize(): number
    {
        return this._pageSize
    }

    set pageIndex(value: number)
    {
        this._pageIndex = value
        this.clinicService.tableSettings.pageIndex = value
    }

    get pageIndex(): number
    {
        return this._pageIndex
    }

    constructor(private clinicService: ClinicService)
    {
        super();
    }

    /**
     * Connect this data source to the table. The table will only update when
     * the returned stream emits new items.
     * @returns A stream of the items to be rendered.
     */

    connect(): Observable<ClinicListItem[]>
    {
        return this.clinicsSubject.asObservable();
    }

    /**
     *  Called when the table is being destroyed. Use this function, to clean up
     * any open connections or free any held resources that were set up during connect.
     */

    disconnect()
    {
        this.clinicsSubject.complete();
        this.loadingSubject.complete();
        this.subscription.unsubscribe()
    }


    loadClinics()
    {
        this.loadingSubject.next(true)
        this.clinicsSubject.next([])
        if (this.sortDirection != SortingDirectionsEnum.none) this.sortData();
        let l_sub = this.clinicService.loadedClinicListSubject.pipe(first(val => val == true)).subscribe(() =>
        {
            this._data = this.clinicService.clinicList.slice()
            if (this.sortDirection != SortingDirectionsEnum.none) this.sortData();

            if (this.clinicService.filterTableByClinic.value.length > 0)
            {
                this._data = this._data.filter(l_user => 
                    l_user.clinicId?.toLowerCase().startsWith(this.clinicService.filterTableByClinic.value.toLowerCase())||
                    l_user.clinicName?.toLowerCase().startsWith(this.clinicService.filterTableByClinic.value.toLowerCase())||
                    l_user.addressLine1?.toLowerCase().startsWith(this.clinicService.filterTableByClinic.value.toLowerCase())||
                    l_user.addressLine2?.toLowerCase().startsWith(this.clinicService.filterTableByClinic.value.toLowerCase())||
                    l_user.city?.toLowerCase().startsWith(this.clinicService.filterTableByClinic.value.toLowerCase())
                )
            }
            this.loadClinicsPage();

            this.loadingSubject.next(false)
        })


        let l_sub1 = this.clinicService.filterTableByClinic.pipe(skip(1)).subscribe(() =>
        {
            this._data = this.clinicService.clinicList.slice();
            this._data = this._data.filter(l_user => 
                l_user.clinicId?.toLowerCase().startsWith(this.clinicService.filterTableByClinic.value.toLowerCase())||
                l_user.clinicName?.toLowerCase().startsWith(this.clinicService.filterTableByClinic.value.toLowerCase())||
                l_user.addressLine1?.toLowerCase().startsWith(this.clinicService.filterTableByClinic.value.toLowerCase())||
                l_user.addressLine2?.toLowerCase().startsWith(this.clinicService.filterTableByClinic.value.toLowerCase())||
                l_user.city?.toLowerCase().startsWith(this.clinicService.filterTableByClinic.value.toLowerCase())
            )

            this.loadClinicsPage();
        })

        this.subscription.add(l_sub)
        this.subscription.add(l_sub1)
    }


    loadClinicsPage()
    {
        let l_result = this._data.slice(this.pageIndex * this.pageSize, (this.pageIndex + 1) * this.pageSize)
        this.clinicsSubject.next(l_result)
    }


    sortData()
    {
        if (this.sortDirection == SortingDirectionsEnum.none)
        {
            this._data = this._data.sort(() => Math.random() - 0.5)
        }
        else
        {
            switch (this.sortingHeader)
            {
                case SelectClinicsSortingHeadersEnum.id:
                    {
                        this.simpleSort(SelectClinicsSortingKeysEnum.clinicId);
                        break;
                    }
                case SelectClinicsSortingHeadersEnum.name:
                    {
                        this.simpleSort(SelectClinicsSortingKeysEnum.ClinicName);
                        break;
                    }
                case SelectClinicsSortingHeadersEnum.address:
                    {
                        this.addressSort();
                        break;
                    }
            }
        }
    }



    simpleSort(key: string)
    {
        let l_returnTrue = 1
        let l_returnFalse = -1

        if (this.sortDirection == SortingDirectionsEnum.desc)
        {
            l_returnTrue = -1
            l_returnFalse = 1
        }

        this._data.sort((obj1, obj2) =>
        {
            if (Object.keys(obj1).includes(key))
            {
                if (!(obj1 as any)[key])
                {
                    return l_returnTrue
                }

                if (!(obj2 as any)[key])
                {
                    return l_returnFalse
                }
                if ((obj1 as any)[key] > (obj2 as any)[key])
                {
                    return l_returnTrue;
                }

                if ((obj1 as any)[key] < (obj2 as any)[key])
                {
                    return l_returnFalse;
                }
            } else
            {
                throw new Error(`Key ${ key } does not found in keys of object of type ClinicListItem`)
            }
            return 0;
        });
    }

    addressSort()
    {
        let l_returnTrue = 1
        let l_returnFalse = -1
        if (this.sortDirection == SortingDirectionsEnum.desc)
        {
            l_returnTrue = -1
            l_returnFalse = 1
        }

        this._data.sort((obj1, obj2) =>
        {

            if ((obj1.addressLine1 + obj1.addressLine2) > (obj2.addressLine1 + obj2.addressLine2))
            {
                return l_returnTrue;
            }

            if ((obj1.addressLine1 + obj1.addressLine2) < (obj2.addressLine1 + obj2.addressLine2))
            {
                return l_returnFalse;
            }

            return 0;
        });
    }


}
